// src/screens/ShortReceipt.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToShortGuestReservations } from "../../../../redux/shortstayReservationSlice";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import logo from "../../../../assets/app/logo.png";

const euro = "€";

const ShortReceipt: React.FC = () => {
  const { guestId, reservationId } = useParams<{
    guestId: string;
    reservationId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const [expanded, setExpanded] = useState(false);

  const reservations = useSelector(
    (s: RootState) => s.shortstayReservations.guestReservations
  );
  const listing = useSelector((s: RootState) => s.shortstays.currentListing);
  const allUsers = useSelector((s: RootState) => s.auth.allUsers);

  const booking = reservations.find((r) => r.id === reservationId);
  const hostUser = allUsers.find((u) => u.userId === booking?.hostId);
  const guestUser = allUsers.find((u) => u.userId === booking?.guestId);

  useEffect(() => {
    if (guestId) dispatch(listenToShortGuestReservations(guestId));
    if (booking?.listingId) dispatch(listenToShortstayData(booking.listingId));
    if (!allUsers.length) dispatch(fetchAllUsers());
  }, [dispatch, guestId, booking?.listingId, allUsers.length]);

  const getStatusLabel = (b: any): string => {
    if (b.isCancelled) return "Cancelled";
    if (b.isRefunded) return "Refunded";
    if (b.isConfirmed && b.isPaid) return "Confirmed & Paid";
    if (b.isConfirmed) return "Confirmed";
    if (b.isOngoing) return "Ongoing Stay";
    if (b.isCompleted) return "Completed";
    if (b.isArrivingSoon) return "Arriving Soon";
    if (b.isUpcoming) return "Upcoming";
    return "Pending";
  };

  if (!booking) {
    return (
      <div style={styles.loading}>
        <p>Loading receipt...</p>
      </div>
    );
  }

  const isHourly =
    booking.rateType === "perHour" || booking.rateType === "perHourPerPerson";

  const durationLabel = isHourly ? "Hours" : "Nights";
  const durationValue = isHourly
    ? booking.totalHours || 0
    : booking.reservedDuration || 1;

  const hostName = hostUser
    ? `${hostUser.firstName || ""} ${hostUser.lastName || ""}`.trim()
    : "—";

  const locationLabel = [
    listing?.address?.city,
    listing?.address?.country,
  ]
    .filter(Boolean)
    .join(", ") || "";

  // ── helpers ─────────────────────────────────────────────────────────────
  const fetchImageAsBase64 = (url: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")!.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = reject;
      img.src = url;
    });

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // ── PDF ──────────────────────────────────────────────────────────────────
  const downloadPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const guestName = guestUser?.firstName || "Guest";

    // gold outer border
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(1.5);
    doc.roundedRect(8, 8, pageWidth - 16, pageHeight - 16, 10, 10, "S");

    // black header bar
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, pageWidth, 32, "F");

    doc.addImage(logo, "PNG", 10, 5, 18, 18);
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text("ARELUXE", 32, 20);

    // greeting
    let y = 50;
    doc.setFontSize(18);
    doc.setTextColor(212, 175, 55);
    doc.setFont("helvetica", "bold");
    doc.text(`Hi ${guestName},`, pageWidth / 2, y, { align: "center" });

    y += 10;
    doc.setFontSize(11);
    doc.setTextColor(85, 85, 85);
    doc.setFont("helvetica", "normal");
    doc.text("Your booking is officially confirmed.", pageWidth / 2, y, {
      align: "center",
    });

    // listing title
    y += 12;
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(listing?.title || "Your Stay", pageWidth / 2, y, {
      align: "center",
    });

    // location
    if (locationLabel) {
      y += 8;
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 120);
      doc.setFont("helvetica", "normal");
      doc.text(locationLabel, pageWidth / 2, y, { align: "center" });
    }

    // listing image
    y += 8;
    const photo = listing?.photos?.[0];
    if (photo) {
      try {
        const imgData = await fetchImageAsBase64(photo);
        const imgW = pageWidth - 40;
        const imgH = 55;
        doc.addImage(imgData, "JPEG", 20, y, imgW, imgH, undefined, "FAST");
        y += imgH + 10;
      } catch {
        y += 6;
      }
    }

    // check-in / check-out box
    const boxH = 28;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.roundedRect(20, y, pageWidth - 40, boxH, 5, 5, "S");

    const boxTop = y + 9;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("CHECK-IN", 30, boxTop);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(fmtDate(booking.checkIn), 30, boxTop + 9);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("CHECK-OUT", pageWidth / 2 + 5, boxTop);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(fmtDate(booking.checkOut), pageWidth / 2 + 5, boxTop + 9);

    y += boxH + 10;

    // gold payment box
    const payH = 38;
    doc.setFillColor(255, 250, 240);
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.8);
    doc.roundedRect(20, y, pageWidth - 40, payH, 6, 6, "FD");

    const payTop = y + 10;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `${durationValue} ${durationLabel} × ${euro}${(
        (booking.totalPaid ?? 0) - (booking.guestsFee ?? 0)
      ).toFixed(2)}`,
      30,
      payTop
    );
    doc.text(
      `Service fee: ${euro}${(booking.guestsFee ?? 0).toFixed(2)}`,
      30,
      payTop + 10
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(212, 175, 55);
    doc.text(
      `Total Paid: EUR ${(booking.totalPaid ?? 0).toFixed(2)}`,
      30,
      payTop + 22
    );

    doc.save("areluxe_receipt.pdf");
  };

  // ── UI ───────────────────────────────────────────────────────────────────
  const photo = listing?.photos?.[0] || logo;

  return (
    <div style={styles.page}>
      <motion.div
        style={styles.bookCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div style={styles.bookHeader} onClick={() => setExpanded(!expanded)}>
          <h3 style={styles.bookTitle}>
            {listing?.title || "Shortstay Booking Receipt"}
          </h3>
          {expanded ? (
            <ExpandLess style={{ color: "#bf9b30" }} />
          ) : (
            <ExpandMore style={{ color: "#bf9b30" }} />
          )}
        </div>

        <div style={styles.coverSection}>
          <img src={photo} alt="Listing" style={styles.image} />
          <div style={styles.coverOverlay}>
            <h4 style={styles.coverTitle}>Total Paid</h4>
            <p style={styles.coverAmount}>
              {euro}
              {(booking.totalPaid ?? 0).toFixed(2)}
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: expanded ? 1 : 0,
            height: expanded ? "auto" : 0,
          }}
          transition={{ duration: 0.5 }}
          style={styles.whitePage}
        >
          <div style={styles.pageTop} />
          <div style={styles.pageContent}>

            {locationLabel ? (
              <div style={{ ...styles.tableRow, color: "#888", fontSize: 12 }}>
                <span>Location</span>
                <span>{locationLabel}</span>
              </div>
            ) : null}

            <div style={styles.tableRow}>
              <span>Check-In</span>
              <span>{new Date(booking.checkIn).toLocaleDateString("en-GB")}</span>
            </div>
            <div style={styles.tableRow}>
              <span>Check-Out</span>
              <span>{new Date(booking.checkOut).toLocaleDateString("en-GB")}</span>
            </div>
            <div style={styles.tableRow}>
              <span>Guests</span>
              <span>{booking.guestCount || 1}</span>
            </div>
            <div style={styles.tableRow}>
              <span>Total {durationLabel}</span>
              <span>{durationValue}</span>
            </div>
            <div style={styles.tableRow}>
              <span>Service Fee</span>
              <span>{euro}{(booking.guestsFee ?? 0).toFixed(2)}</span>
            </div>
            <div style={styles.tableRow}>
              <span>Total Paid</span>
              <span style={{ color: "#bf9b30", fontWeight: 700 }}>
                {euro}{(booking.totalPaid ?? 0).toFixed(2)}
              </span>
            </div>
            <div style={styles.tableRow}>
              <span>Status</span>
              <span>{getStatusLabel(booking)}</span>
            </div>

            <h4 style={styles.sectionHeader}>Host</h4>
            <div style={styles.tableRow}>
              <span>Name</span>
              <span>{hostName}</span>
            </div>

            <button onClick={downloadPDF} style={styles.downloadBtn}>
              📥 Download PDF Receipt
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ShortReceipt;

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles: { [key: string]: React.CSSProperties } = {
  page: {
    background: "#000",
    color: "#fff",
    minHeight: "100vh",
    padding: "60px 20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Poppins, sans-serif",
  },
  bookCard: {
    background:
      "linear-gradient(145deg,#0b0d18,#1a0e25) padding-box,linear-gradient(135deg,#bf9b30,#ffb347,#fff) border-box",
    border: "2px solid transparent",
    borderRadius: 20,
    padding: 24,
    width: "95%",
    maxWidth: 480,
    boxShadow:
      "0 10px 25px rgba(0,0,0,.5), inset 0 0 12px rgba(255,255,255,.05)",
  },
  bookHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  bookTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#bf9b30",
    margin: 0,
  },
  coverSection: {
    position: "relative",
    marginTop: 16,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 6px 18px rgba(191,155,48,0.4)",
  },
  image: { width: "100%", height: 200, objectFit: "cover" },
  coverOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(transparent, rgba(0,0,0,0.9))",
    padding: "12px 16px",
  },
  coverTitle: { fontSize: 14, color: "#ddd", margin: 0 },
  coverAmount: {
    fontSize: 28,
    fontWeight: 800,
    color: "#ffb347",
    margin: 0,
  },
  whitePage: {
    background: "#fff",
    borderRadius: 14,
    marginTop: 16,
    overflow: "hidden",
    boxShadow: "inset 0 0 8px rgba(0,0,0,.05), 0 3px 12px rgba(0,0,0,.25)",
  },
  pageTop: {
    height: 4,
    background: "repeating-linear-gradient(90deg,#ddd 0 10px,#fff 10px 20px)",
  },
  pageContent: {
    padding: "16px 20px",
    background: "repeating-linear-gradient(#fff, #fff 28px, #f7f7f7 29px)",
  },
  tableRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 14,
    color: "#222",
    padding: "8px 0",
    borderBottom: "1px dashed rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    marginTop: 16,
    marginBottom: 8,
    fontWeight: 700,
    color: "#bf9b30",
    fontSize: 14,
  },
  downloadBtn: {
    width: "100%",
    marginTop: 20,
    background: "linear-gradient(145deg,#bf9b30,#ffb347,#fff)",
    border: "none",
    borderRadius: 40,
    color: "#000",
    fontWeight: 700,
    fontSize: 16,
    padding: "12px 0",
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
  },
  loading: {
    background: "#000",
    color: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
};